import {
  ValidationService,
  type FormService,
  type Group,
  type IValidationService,
  type Question,
  type ValidationResponse,
} from '@stepform';
import {
  getArrayTotal,
  getMinMaxValidationMessage,
  hasValue,
  inAllowedRange,
  isInteger,
  nonNegative,
  validateMinTotal,
} from './utils/arrayField';
import type { NotifiableEventsFieldSchema } from './components/NotifiableEventsField';
import type { SplitQuantityFieldSchema } from './components/SplitQuantityField';

export class ExtractivesValidationService extends ValidationService implements IValidationService {
  constructor(formService: FormService) {
    super(formService);
  }

  public validate(question: Question, group: Group): ValidationResponse {
    const validationResponse = super.validate(question, group);

    if (!validationResponse.valid) {
      return validationResponse;
    }

    if (group.id === 'types-of-injuries-illnesses' && question === group.questions[0]) {
      return this.validateInjuryTypesGroup(group);
    }

    if (group.id === 'workers-notifiable-events' && question.id === 'OperationHours') {
      return this.validateWorkersAndHoursGroup(group);
    }

    if (group.id === 'days-lost-alternative-duties' && question.id === 'DaysLost') {
      return this.validateLostTime(question);
    }

    if (group.id === 'days-lost-alternative-duties' && question.id === 'DaysAltDuty') {
      return this.validateAlternativeDuty(question);
    }

    return validationResponse;
  }

  protected validateQuestionType(question: Question): ValidationResponse {
    switch (question.type) {
      case 'NZBN':
        return this.validateNzbnType(question);
      case 'SplitQuantity':
        return this.validateSplitQuantityType(question);
      case 'NotifiableEvents':
        return this.validateNotifiableEventsType(question);
      case 'Name':
        return this.validateNameType(question);
      default:
        return super.validateQuestionType(question);
    }
  }

  private validateInjuryTypesGroup(group: Group): ValidationResponse {
    const totalSum = group.questions.reduce((sum, groupQuestion) => {
      if (groupQuestion.type === 'SplitQuantity') {
        return sum + getArrayTotal(groupQuestion.answer);
      }

      return sum;
    }, 0);

    const validationMessage = group.questions
      .filter((groupQuestion) => groupQuestion.type === 'SplitQuantity')
      .map((groupQuestion) => `&bull; ${groupQuestion.label}`)
      .join(' <strong>OR</strong><br>');

    return totalSum > 0
      ? this.createResponse(true)
      : this.createResponse(
          false,
          `At least one non-zero number needs to be entered in:<br> ${validationMessage}`
        );
  }

  private validateWorkersAndHoursGroup(group: Group): ValidationResponse {
    const workerQuestion = group.questions[0];
    const hoursQuestion = group.questions[1];

    if (!workerQuestion || !hoursQuestion) {
      return this.createResponse(true);
    }

    const workerAnswer = Array.isArray(workerQuestion.answer) ? workerQuestion.answer : [0, 0];
    const hoursAnswer = Array.isArray(hoursQuestion.answer) ? hoursQuestion.answer : [0, 0];
    const [employeeCount, otherCount] = workerAnswer;
    const [employeeHours, otherHours] = hoursAnswer;

    const errorMessages = [
      employeeCount > 0 && employeeHours === 0
        ? 'You have mentioned employees were at the operation. Please tell us how many hours they worked.'
        : undefined,
      employeeHours > 0 && employeeCount === 0
        ? 'You have entered hours for employees. Tell us how many were at the operation in the previous question.'
        : undefined,
      otherCount > 0 && otherHours === 0
        ? 'You have mentioned other workers were at the operation. Please tell us how many hours they worked.'
        : undefined,
      otherHours > 0 && otherCount === 0
        ? 'You have entered hours for other workers. Tell us how many were at the operation in the previous question.'
        : undefined,
    ].filter(Boolean);

    return errorMessages.length
      ? this.createResponse(false, errorMessages.join('<br><br>'))
      : this.createResponse(true);
  }

  private validateAlternativeDuty(question: Question): ValidationResponse {
    const countAltDuty = this.formService.getQuestionById('InjuriesAltDuty');

    if (!countAltDuty || !this.isQuestionVisible(countAltDuty)) {
      return this.createResponse(true);
    }

    const countAnswer = Array.isArray(countAltDuty.answer) ? countAltDuty.answer : [0, 0];
    const daysAnswer = Array.isArray(question.answer) ? question.answer : [0, 0];
    const [countEmployees, countOther] = countAnswer;
    const [daysEmployees, daysOther] = daysAnswer;

    const errorMessages = [
      countEmployees > 0 && daysEmployees === 0
        ? 'On the previous page, you mentioned employees were affected by alternative duty injuries or illnesses. Enter the number of alternative duty days worked below. Or, select ‘Back’ if you made a mistake.'
        : undefined,
      countOther > 0 && daysOther === 0
        ? 'On the previous page, you mentioned other workers were affected by alternative duty injuries or illnesses. Enter the number of alternative duty days worked below. Or, select ‘Back’ if you made a mistake.'
        : undefined,
    ].filter(Boolean);

    return errorMessages.length
      ? this.createResponse(false, errorMessages.join('<br><br>'))
      : this.createResponse(true);
  }

  private validateLostTime(question: Question): ValidationResponse {
    const countLostTime = this.formService.getQuestionById('InjuriesTime');

    if (!countLostTime || !this.isQuestionVisible(countLostTime)) {
      return this.createResponse(true);
    }

    const countAnswer = Array.isArray(countLostTime.answer) ? countLostTime.answer : [0, 0];
    const daysAnswer = Array.isArray(question.answer) ? question.answer : [0, 0];
    const [countEmployees, countOther] = countAnswer;
    const [daysEmployees, daysOther] = daysAnswer;

    const errorMessages = [
      countEmployees > 0 && daysEmployees === 0
        ? 'On the previous page, you mentioned employees were affected by lost-time injuries or illnesses. Enter the number of days lost below. Or, select ‘Back’ if you made a mistake.'
        : undefined,
      countOther > 0 && daysOther === 0
        ? 'On the previous page, you mentioned other workers were affected by lost-time injuries or illnesses. Enter the number of days lost below. Or, select ‘Back’ if you made a mistake.'
        : undefined,
    ].filter(Boolean);

    return errorMessages.length
      ? this.createResponse(false, errorMessages.join('<br><br>'))
      : this.createResponse(true);
  }

  private validateNameType(question: Question): ValidationResponse {
    const split = String(question.answer ?? '').trim().split(/\s+/).filter(Boolean);
    return split.length > 1 ? this.createResponse(true) : this.createResponse(false, 'Required');
  }

  private validateNzbnType(question: Question): ValidationResponse {
    const answer = String(question.answer ?? '');

    if (answer && !/^[0-9]{13}$/.test(answer)) {
      return this.createResponse(
        false,
        'Please enter a valid NZBN. This is a 13-digit number assigned to your business or organisation.'
      );
    }

    return this.createResponse(true);
  }

  private validateSplitQuantityType(question: Question): ValidationResponse {
    return this.validateArrayQuantityType(question, {
      minTotalMessage:
        'At least one non-zero number needs to be entered in Employees OR Other workers',
    });
  }

  private validateNotifiableEventsType(question: Question): ValidationResponse {
    return this.validateArrayQuantityType(question, {
      minTotalMessage:
        'At least one non-zero number needs to be entered in Employees OR Other workers OR Employees and other workers',
    });
  }

  private validateArrayQuantityType(
    question: Question,
    options: { minTotalMessage: string }
  ): ValidationResponse {
    const fieldSchema = this.getArrayFieldSchema(question);
    const { answer } = question;
    const values = Array.isArray(answer) ? answer : [];

    if (!values.length || !values.every(hasValue)) {
      return this.createResponse(false, 'Required');
    }

    if (!values.every((value) => inAllowedRange(value, fieldSchema.min, fieldSchema.max))) {
      return this.createResponse(
        false,
        getMinMaxValidationMessage(fieldSchema.min, fieldSchema.max) ?? 'Required'
      );
    }

    if (!values.every(nonNegative)) {
      return this.createResponse(false, 'Your answer must be greater than or equal to 0');
    }

    if (!values.every(isInteger)) {
      return this.createResponse(false, 'Please round you answers to a whole number');
    }

    return validateMinTotal(answer, fieldSchema.minTotal)
      ? this.createResponse(true)
      : this.createResponse(false, options.minTotalMessage);
  }

  private getArrayFieldSchema(
    question: Question
  ): SplitQuantityFieldSchema | NotifiableEventsFieldSchema {
    return {
      type: question.type as 'SplitQuantity' | 'NotifiableEvents',
      id: question.id,
      min: 0,
      max: 1e9,
      minTotal: ['OperationNumbers', 'OperationHours', 'CombinedNotifiableEvents', 'CombinedFatalities'].includes(
        question.id
      )
        ? 1
        : undefined,
    };
  }

  private isQuestionVisible(question: Question): boolean {
    for (const step of this.formService.getFormData().steps) {
      for (const section of step.sections) {
        for (const group of section.groups) {
          const match = group.questions.find((groupQuestion) => groupQuestion.id === question.id);
          if (match) {
            return true;
          }
        }
      }
    }

    return false;
  }
}
